import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiCheck, FiHelpCircle, FiChevronDown, FiChevronUp } from 'react-icons/fi';

const QuestionCard = ({ question, onAnswer, userResponse = null, isCompleted = false }) => {
  const [response, setResponse] = useState(userResponse || '');
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(!!userResponse);

  const handleSubmit = () => {
    if (response.trim()) {
      onAnswer(question._id, response);
      setHasAnswered(true);
    }
  };

  // Render different question types based on the type field
  const renderQuestionInput = () => {
    switch (question.type) {
      case 'multiple_choice':
        return (
          <div className="space-y-2 mt-4">
            {question.options.map((option, index) => (
              <div key={index} className="flex items-center">
                <input 
                  type="radio" 
                  id={`option-${index}`}
                  name={`question-${question._id}`}
                  value={option}
                  checked={response === option}
                  onChange={() => setResponse(option)}
                  disabled={isCompleted || hasAnswered}
                  className="custom-checkbox"
                />
                <label 
                  htmlFor={`option-${index}`} 
                  className="ml-2 text-gray-700"
                >
                  {option}
                </label>
              </div>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-2 mt-4">
            {question.options.map((option, index) => {
              const selectedOptions = response ? response.split(',') : [];
              const isChecked = selectedOptions.includes(option);
              
              return (
                <div key={index} className="flex items-center">
                  <input 
                    type="checkbox" 
                    id={`checkbox-${index}`}
                    value={option}
                    checked={isChecked}
                    onChange={() => {
                      let newSelected;
                      if (isChecked) {
                        newSelected = selectedOptions.filter(item => item !== option);
                      } else {
                        newSelected = [...selectedOptions, option];
                      }
                      setResponse(newSelected.join(','));
                    }}
                    disabled={isCompleted || hasAnswered}
                    className="custom-checkbox"
                  />
                  <label 
                    htmlFor={`checkbox-${index}`} 
                    className="ml-2 text-gray-700"
                  >
                    {option}
                  </label>
                </div>
              );
            })}
          </div>
        );

      case 'text':
        return (
          <div className="mt-4">
            <textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder="Type your answer here..."
              className="form-input w-full min-h-[100px]"
              disabled={isCompleted || hasAnswered}
            />
          </div>
        );

      default:
        return (
          <div className="mt-4">
            <input
              type="text"
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder="Type your answer here..."
              className="form-input w-full"
              disabled={isCompleted || hasAnswered}
            />
          </div>
        );
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-lg border shadow-card p-6 mb-6 transition-all duration-300 ${hasAnswered ? 'border-l-4 border-success' : 'border-l-4 border-brandBlue'}`}
    >
      <div className="flex justify-between items-start">
        <div className="flex-grow">
          <div className="flex items-start">
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${hasAnswered ? 'bg-success text-white' : 'bg-brandLight text-brandDark'}`}>
              {hasAnswered ? <FiCheck /> : <FiHelpCircle />}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{question.title}</h3>
              {question.description && (
                <p className="text-gray-600 mt-1">{question.description}</p>
              )}
            </div>
          </div>

          {renderQuestionInput()}

          {question.helpText && (
            <div className="mt-4">
              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center text-brandBlue hover:underline"
              >
                {isExpanded ? (
                  <>
                    <FiChevronUp className="mr-1" /> Hide help
                  </>
                ) : (
                  <>
                    <FiChevronDown className="mr-1" /> Show help
                  </>
                )}
              </button>
              
              {isExpanded && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-gray-50 p-4 rounded-lg mt-2 text-gray-700"
                >
                  {question.helpText}
                </motion.div>
              )}
            </div>
          )}
        </div>
      </div>

      {!hasAnswered && !isCompleted && (
        <motion.button
          onClick={handleSubmit}
          className="btn btn-primary mt-4"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          disabled={!response.trim()}
        >
          Submit Answer
        </motion.button>
      )}

      {hasAnswered && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-4 bg-green-50 p-3 rounded-lg flex items-center"
        >
          <FiCheck className="text-success mr-2" />
          <span className="text-green-800">Answer submitted! Thank you for your response.</span>
        </motion.div>
      )}
    </motion.div>
  );
};

export default QuestionCard; 